import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, User } from 'lucide-react';
import { useSeats } from '../../api/seats';
import { MarkdownRenderer } from '../MarkdownRenderer';
import type { Seat } from '../../types';

const TEAM_COLORS: Record<string, string> = {
  Leadership: 'bg-purple-100 text-purple-900 dark:bg-purple-900/30 dark:text-purple-200',
  Sales: 'bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-200',
  Operations: 'bg-orange-100 text-orange-900 dark:bg-orange-900/30 dark:text-orange-200',
  RnD: 'bg-green-100 text-green-900 dark:bg-green-900/30 dark:text-green-200',
  Finances: 'bg-yellow-100 text-yellow-900 dark:bg-yellow-900/30 dark:text-yellow-200',
  System: 'bg-gray-100 text-gray-900 dark:bg-gray-900/30 dark:text-gray-200',
};

function holderName(h: Seat['holders'][number]): string {
  return [h.firstname, h.lastname].filter(Boolean).join(' ') || h.email || 'Unknown';
}

export function SeatsTab() {
  const navigate = useNavigate();
  const { data, isLoading } = useSeats();
  const seats = data?.seats ?? [];

  const [search, setSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState<string | null>(null);

  const teamNames = useMemo(() => {
    const set = new Set<string>();
    for (const s of seats) for (const t of s.teams) set.add(t.name);
    return Array.from(set).sort();
  }, [seats]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return seats.filter((s) => {
      if (teamFilter && !s.teams.some((t) => t.name === teamFilter)) return false;
      if (!q) return true;
      const teamStr = s.teams.map((t) => t.name).join(' ').toLowerCase();
      const holderStr = s.holders.map(holderName).join(' ').toLowerCase();
      return (
        s.title.toLowerCase().includes(q) ||
        holderStr.includes(q) ||
        teamStr.includes(q) ||
        (s.description ?? '').toLowerCase().includes(q)
      );
    });
  }, [seats, search, teamFilter]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-48 rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search seats…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => setTeamFilter(null)}
            className={`text-xs px-2 py-1 rounded border ${
              teamFilter === null ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'
            }`}
          >
            All
          </button>
          {teamNames.map((team) => (
            <button
              key={team}
              type="button"
              onClick={() => setTeamFilter(team === teamFilter ? null : team)}
              className={`text-xs px-2 py-1 rounded border ${
                team === teamFilter
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border hover:bg-muted'
              }`}
            >
              {team}
            </button>
          ))}
        </div>
        <div className="ml-auto text-xs text-muted-foreground">
          {filtered.length} of {seats.length} seats
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filtered.map((seat) => (
          <Card
            key={seat.id}
            className="flex flex-col cursor-pointer hover:border-primary/40 hover:shadow-md transition-all"
            onClick={() => navigate(`/os/accountability/seats/${seat.id}`)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-lg">{seat.title}</CardTitle>
                <div className="flex flex-wrap gap-1 justify-end">
                  {seat.teams.map((t) => (
                    <Badge
                      key={t.id}
                      variant="secondary"
                      className={TEAM_COLORS[t.name] ?? ''}
                    >
                      {t.name}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground flex-wrap">
                <User className="h-3.5 w-3.5 shrink-0" />
                {seat.holders.length === 0 ? (
                  <Badge variant="outline" className="bg-zinc-50 text-zinc-500 text-[10px]">Vacant</Badge>
                ) : (
                  seat.holders.map((h, i) => (
                    <span key={h.id} className="inline-flex items-center">
                      {h.profile_photo_url ? (
                        <img
                          src={h.profile_photo_url}
                          alt=""
                          className="h-4 w-4 rounded-full object-cover mr-1"
                        />
                      ) : null}
                      <a
                        href={`/hris/employees/${h.user_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-primary hover:underline"
                      >
                        {holderName(h)}
                      </a>
                      {h.territory && <span className="ml-1 text-muted-foreground">({h.territory})</span>}
                      {i < seat.holders.length - 1 && <span className="mx-1">·</span>}
                    </span>
                  ))
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {seat.description && <MarkdownRenderer>{seat.description}</MarkdownRenderer>}
            </CardContent>
          </Card>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center text-sm text-muted-foreground py-12">
          No seats match your filters.
        </div>
      )}
    </div>
  );
}
